```vue
<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick, watch } from "vue";
import ProductsSection from "./components/ProductsSection.vue";
import {
  CashierDuty,
  Item,
  ItemCategory,
  ItemGroup,
  PaymentType,
  PosStation,
  RestaurantTables,
  SalesRep,
} from "../../../../types/models";
import { User } from "../../../../types/auth";
import TableLayout from "./restaurant/TableLayout.vue";
import { posStore } from "../../../../stores/posStore";
import TableSummary from "./restaurant/TableSummary.vue";
import { api } from "../../../../api/Api";
import OrderDetails from "./restaurant/OrderDetails.vue";
import CheckoutModal from "./components/CheckoutModal.vue";
import { useScreenSize } from "../../../../utils/useScreenSize";
import MobileSummary from "./restaurant/MobileSummary.vue";

const { isMobile, isTablet } = useScreenSize();

const pos = posStore();

const props = defineProps<{
  isFullscreen: boolean;
  payment_types: PaymentType[];
  item_categories: ItemCategory[];
  item_groups: ItemGroup[];
  delivery_staffs: User[];
  posStation: PosStation | undefined;
  cashierDuty: CashierDuty | null;
  salesReps: SalesRep[];
  restaurantTables: RestaurantTables[];
}>();

const emit = defineEmits<{
  (e: "addItem", item: Item): void;
  (e: "close"): void;
  (e: "back"): void;
  (e: "order-created", tables: RestaurantTables[]): void;
  (e: "table-selected", table: RestaurantTables): void;
}>();

const endPoint = "revenue/sales/pos/";

const productsSectionRef = ref<InstanceType<typeof ProductsSection> | null>(
  null
);

// Expose focusSearch method for parent component
defineExpose({
  focusSearch() {
    productsSectionRef.value?.focusSearch();
  },
  refreshTables() {
    console.log("Refreshing tables from POSView");
    getTables();
  },
  setTables(tablesList: RestaurantTables[]) {
    updateTables(tablesList);
    console.log("Tables set from POSView:", tablesList);
  },
  viewBill(table: RestaurantTables) {
    showBill(table);
  },
});

onMounted(() => {
  productsSectionRef.value?.focusSearch();
  tables.value =
    props.posStation?.outlet?.tables || props.restaurantTables || [];
  //window.addEventListener('pos-checkout', handleCheckout)

  setInterval(() => getTables(false), 300000);
});

onUnmounted(() => {
  //window.removeEventListener('pos-checkout', handleCheckout)
});

function selectTable(table: RestaurantTables) {
  pos.selectedTable = table;
  emit("table-selected", table);
}

const tables = ref<RestaurantTables[]>([]);

const orderCreated = (tablesList: RestaurantTables[]) => {
  console.log("Updating tables in POSView:", tables);
  tables.value = tablesList;
  console.log("restaurantTables after update:", tables.value);
  //emit("order-created", tables);
};

const getTables = async (loader: boolean = true) => {
  const params = {
    outlets_id: props.posStation?.outlet?.uid ?? "",
  };
  const response = await api.post(
    (endPoint.slice(-1) == "/" ? endPoint : endPoint + "/") + "tables",
    params,
    { skipLoading: !loader }
  );
  if (response.success == true) {
    tables.value = response.data;
  }
};

const updateTables = (tablesList: RestaurantTables[]) => {
  tables.value = tablesList;
};

watch(
  () => props.restaurantTables,
  () => {
    tables.value = props.restaurantTables || [];
  }
);

const showOrderDetails = ref(false);
const selectedTable = ref<RestaurantTables | null>(null);
const showBill = (table: RestaurantTables) => {
  selectedTable.value = table;
  showOrderDetails.value = true;
};

const makeBill = async (table: RestaurantTables | null, items: Item[]) => {
  pos.voidInvoice();
  if (pos.selectedPosInvoice?.invoice && table?.orders?.customers_id) {
    pos.selectedPosInvoice.invoice.customers_id =
      table?.orders?.customers_id ?? null;
  }
  items.forEach((item) => {
    pos.addProductToInvoice(item, item.quantity || 1);
  });
  if (pos.selectedPosInvoice?.invoice) {
    pos.selectedPosInvoice.invoice.table_orders_id =
      table?.orders?.uid ?? undefined;
  }
  showOrderDetails.value = false;
  pos.showCheckoutModal = true;
};

const getOrderDetails = async (table: RestaurantTables) => {
  const response = await api.post(
    (endPoint.slice(-1) == "/" ? endPoint : endPoint + "/") + "order-items",
    {
      tables_id: table?.uid,
    }
  );
  if (response.success == true) {
    makeBill(table, response.data);
  }
};

const billCompleted = (_data: any, tablesList: RestaurantTables[]) => {
  pos.voidInvoice();
  updateTables(tablesList);
  console.log("Bill completed, updated tables:", tablesList);
  tables.value = tablesList;
};

const handleNew = () => {
  nextTick(() => {
    productsSectionRef.value?.focusSearch();
  });
};
</script>

<template>
  <div class="h-full">
    <!-- Main Content -->
    <div class="flex gap-6 h-full p-6">
      <MobileSummary v-if="isMobile || isTablet" :restaurantTables="restaurantTables"
      @show-bill="showBill"
          @make-bill="getOrderDetails" />
      <!-- Products Section (Left) -->
      <div class="flex-1 overflow-hidden">
        <TableLayout
          :is-fullscreen="isFullscreen"
          :delivery_staffs="delivery_staffs"
          @table-selected="selectTable"
          :salesReps="salesReps"
          :pos-station="posStation"
          :restaurantTables="tables"
          :cashierDuty="cashierDuty"
          :item_categories="item_categories"
          :item_groups="item_groups"
          :payment_types="payment_types"
        />
      </div>

      <!-- Summary Section (Right) -->
      <div
        v-if="!isMobile && !isTablet"
        class="w-96 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
        :class="[isFullscreen ? '' : 'sticky top-24']"
      >
        <TableSummary
          ref="tableSummaryRef"
          :delivery_staffs="delivery_staffs"
          :restaurant-tables="tables"
          :pos-station="posStation"
          :item_categories="item_categories"
          :salesReps="salesReps"
          :cashierDuty="cashierDuty"
          @show-bill="showBill"
          @make-bill="getOrderDetails"
          @new="handleNew"
          @back="emit('back')"
          @order-created="orderCreated"
          :item_groups="item_groups"
          :payment_types="payment_types"
        />
      </div>
    </div>
    <OrderDetails
      v-if="showOrderDetails"
      @close="showOrderDetails = false"
      :table="selectedTable"
      :cashier-duty="cashierDuty"
      :end-point="endPoint"
      :pos-station="posStation"
      @make-bill="makeBill"
    />

    <CheckoutModal
      v-if="pos.showCheckoutModal"
      :pos-station="posStation"
      :payment_types="payment_types"
      :cashierDuty="cashierDuty"
      :salesReps="props.salesReps"
      :delivery_staffs="delivery_staffs"
      @new="billCompleted"
      :total="pos.net_total ?? 0"
      @close="pos.showCheckoutModal = false"
    />
  </div>
</template>
```
